import { Helmet } from '@dr.pogodin/react-helmet'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import type { ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'

import { useIsRedactionActive } from '../../core/components/Redact'
import { useSafePhone } from '../features/phone/providers/phoneContext'
import { isBusyWithPhoneCall } from '../features/phone/utils/phoneCallBusy'

const DEFAULT_DOCUMENT_TITLE = 'Joyful Health'

interface DocumentTitleEntry {
  id: string
  title: string
}

interface DocumentTitleState {
  pageTitle: string
}

interface DocumentTitleActions {
  removePageTitleEntry: (titleEntryId: string) => void
  setPageTitleEntry: (titleEntry: DocumentTitleEntry) => void
}

type DocumentTitleContextValue = CompositionContextValue<
  DocumentTitleState,
  DocumentTitleActions
>

const DocumentTitleContext =
  createNonNullableContext<DocumentTitleContextValue>('DocumentTitleContext')

export interface DocumentTitleProviderProps {
  children: ReactNode
}

export function DocumentTitleProvider({
  children,
}: DocumentTitleProviderProps) {
  const [titleEntries, setTitleEntries] = useState<Array<DocumentTitleEntry>>(
    [],
  )

  const setPageTitleEntry = (titleEntry: DocumentTitleEntry) => {
    setTitleEntries((currentTitleEntries) => {
      const existingTitleEntry = currentTitleEntries.find((entry) => {
        return entry.id === titleEntry.id
      })

      if (existingTitleEntry) {
        if (existingTitleEntry.title === titleEntry.title) {
          return currentTitleEntries
        }

        return currentTitleEntries.map((entry) => {
          return entry.id === titleEntry.id ? titleEntry : entry
        })
      }

      return [...currentTitleEntries, titleEntry]
    })
  }

  const removePageTitleEntry = (titleEntryId: string) => {
    setTitleEntries((currentTitleEntries) => {
      return currentTitleEntries.filter((entry) => {
        return entry.id !== titleEntryId
      })
    })
  }

  const activeTitleEntry = titleEntries.at(-1)
  const pageTitle = activeTitleEntry
    ? activeTitleEntry.title
    : DEFAULT_DOCUMENT_TITLE

  return (
    <DocumentTitleContext.Context
      value={{
        actions: { removePageTitleEntry, setPageTitleEntry },
        state: { pageTitle },
      }}
    >
      {children}
    </DocumentTitleContext.Context>
  )
}

export function useDocumentTitle() {
  return DocumentTitleContext.useRequiredContext()
}

export interface PageTitleProps {
  children?: string
}

export function PageTitle({ children }: PageTitleProps) {
  const titleEntryId = useId()
  const documentTitleContext = useDocumentTitle()
  const removePageTitleEntry = documentTitleContext.actions.removePageTitleEntry
  const setPageTitleEntry = documentTitleContext.actions.setPageTitleEntry
  const phoneContext = useSafePhone()
  const isRedactionActive = useIsRedactionActive()

  const dynamicTitle = isRedactionActive ? undefined : children
  const baseTitle = [dynamicTitle, DEFAULT_DOCUMENT_TITLE]
    .filter(Boolean)
    .join(' - ')
  const isBusyWithCall =
    phoneContext !== null && isBusyWithPhoneCall(phoneContext.phoneState)
  const pageTitle = [isBusyWithCall ? '📞' : undefined, baseTitle]
    .filter(Boolean)
    .join(' ')

  useEffect(() => {
    setPageTitleEntry({
      id: titleEntryId,
      title: pageTitle,
    })

    return () => {
      removePageTitleEntry(titleEntryId)
    }
  }, [pageTitle, titleEntryId])

  return <Helmet defer={false} title={pageTitle} />
}
